import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import MotionReveal from "./MotionReveal";
import HomeSectionHeading from "./HomeSectionHeading";
import useMotionPreferences from "./useMotionPreferences";

gsap.registerPlugin(ScrollTrigger);

const processVisualPresets = [
  {
    workspaceLabel: "Candidate Intake",
    stateCaption: "Ausgangslage geordnet",
    checklist: [
      { label: "Profilgrundlage", state: "good" },
      { label: "Identitätsnachweis", state: "good" },
      { label: "Qualifikationsunterlagen", state: "pending" },
      { label: "Sprachbelege", state: "pending" },
    ],
    bars: [
      { label: "Dokumententiefe", value: 64 },
      { label: "Vollständigkeit", value: 52 },
      { label: "Prüfbarkeit", value: 43 },
    ],
    detailCards: [
      { label: "Workspace", items: ["Candidate Record", "Dokumentensammlung", "Evidenzlage"] },
      { label: "Lücken", items: ["Sprachdokument", "Lizenznachweis", "Referenzen"] },
    ],
    activity: ["Profil erfasst", "Dokumente sortiert", "Erste Lücken sichtbar"],
  },
  {
    workspaceLabel: "Evidence Layer",
    stateCaption: "Signale werden lesbar",
    checklist: [
      { label: "Signale extrahiert", state: "good" },
      { label: "Lückenlogik erzeugt", state: "good" },
      { label: "Risikohinweise", state: "warn" },
      { label: "Review vorbereitet", state: "good" },
    ],
    bars: [
      { label: "Signalqualität", value: 82 },
      { label: "Strukturgrad", value: 88 },
      { label: "Review-Readiness", value: 71 },
    ],
    detailCards: [
      { label: "Auswertung", items: ["KI-Cluster", "Evidenztypen", "Kontextgewichtung"] },
      { label: "Offene Punkte", items: ["Unklare Dauer", "Nachweisformat", "Berufsnähe"] },
    ],
    activity: ["Dokumente geparst", "Kontext erzeugt", "Review-Layer aktiviert"],
  },
  {
    workspaceLabel: "Readiness Surface",
    stateCaption: "Vorbereitung wird messbar",
    checklist: [
      { label: "Sprachstand", state: "good" },
      { label: "Berufliche Nähe", state: "good" },
      { label: "Integrationsfaktoren", state: "warn" },
      { label: "Vorbereitungspfad", state: "good" },
    ],
    bars: [
      { label: "Sprache", value: 68 },
      { label: "Berufliche Nähe", value: 84 },
      { label: "Ankommens-Readiness", value: 59 },
    ],
    detailCards: [
      { label: "Readiness", items: ["Sprachpfad", "Fachliche Nähe", "Vorbereitungsgrad"] },
      { label: "Empfehlung", items: ["Sprachmodul", "Onboarding-Tiefe", "Wohnraumbedarf"] },
    ],
    activity: ["Readiness berechnet", "Pfad priorisiert", "Ankommensbedarf markiert"],
  },
  {
    workspaceLabel: "Employer View",
    stateCaption: "Matching mit Verantwortung",
    checklist: [
      { label: "Passung", state: "good" },
      { label: "Dokumentenqualität", state: "good" },
      { label: "Arbeitgeberpflichten", state: "good" },
      { label: "Integrationslogik", state: "warn" },
    ],
    bars: [
      { label: "Fit-Signal", value: 86 },
      { label: "Nachweisqualität", value: 78 },
      { label: "Arbeitgeberbereitschaft", value: 66 },
    ],
    detailCards: [
      { label: "Entscheidungsraum", items: ["Kandidatenkontext", "Pflichten", "Welcome-Standards"] },
      { label: "Freigaben", items: ["Wohnraumhilfe", "Welcome-Prämie", "Onboarding-Slots"] },
    ],
    activity: ["Arbeitgeberansicht erzeugt", "Pflichten eingeblendet", "Match-Freigabe vorbereitet"],
  },
  {
    workspaceLabel: "Arrival Flow",
    stateCaption: "Übergabe bleibt geführt",
    checklist: [
      { label: "Filing-Pack", state: "good" },
      { label: "Willkommenslogik", state: "good" },
      { label: "Behördliche Übergaben", state: "pending" },
      { label: "Start in Europa", state: "good" },
    ],
    bars: [
      { label: "Filing-Stand", value: 73 },
      { label: "Onboarding", value: 81 },
      { label: "Integrationsstart", value: 67 },
    ],
    detailCards: [
      { label: "Transfer", items: ["Filing-Status", "Reisevorbereitung", "Welcome-Check"] },
      { label: "Nächste Schritte", items: ["Behördenkontakt", "Wohnraum", "Starttermin"] },
    ],
    activity: ["Filing vorbereitet", "Arrival-Plan aktiv", "Übergabe bleibt sichtbar"],
  },
];

function ProcessVisual({ step, index, total, reducedMotion }) {
  const preset = processVisualPresets[index] || processVisualPresets[0];
  const resolvedCount = preset.checklist.filter((item) => item.state === "good").length;
  const openCount = preset.checklist.filter((item) => item.state !== "good").length;

  return (
    <AnimatePresence mode="wait">
      <motion.article
        key={step.step}
        className="public-home-premium__process-workspace"
        initial={false}
        animate={reducedMotion ? undefined : { opacity: 1, y: 0, scale: 1 }}
        exit={reducedMotion ? undefined : { opacity: 0, y: -12, scale: 0.985 }}
        transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="public-home-premium__process-visual-head">
          <div className="public-home-premium__process-head-copy">
            <span>{preset.workspaceLabel}</span>
            <strong>{step.metricValue}</strong>
            <p>{step.panelText}</p>
          </div>
          <div className="public-home-premium__process-head-meta">
            <span className="public-home-premium__process-status-badge is-live">{step.metricLabel}</span>
            <span className="public-home-premium__process-status-badge">
              {step.step} / {String(total).padStart(2, "0")}
            </span>
          </div>
        </div>

        <div className="public-home-premium__process-signal-row">
          {(step.highlights || []).map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>

        <div className="public-home-premium__process-dashboard">
          <aside className="public-home-premium__process-sidebar-panel">
            <span className="public-home-premium__mini-label">Systemstatus</span>
            <ul className="public-home-premium__process-checklist">
              {preset.checklist.map((item) => (
                <li key={item.label} className={`public-home-premium__process-check public-home-premium__process-check--${item.state}`.trim()}>
                  <i aria-hidden="true" />
                  <span>{item.label}</span>
                </li>
              ))}
            </ul>
          </aside>

          <div className="public-home-premium__process-main-panel">
            <div className="public-home-premium__process-main-head">
              <div className="flex flex-col gap-2">
                <span className="public-home-premium__mini-label">{step.panelTitle}</span>
                <strong>{step.title}</strong>
              </div>
              <span className="public-home-premium__process-state-caption">{preset.stateCaption}</span>
            </div>

            <div className="public-home-premium__process-bars">
              {preset.bars.map((bar) => (
                <div key={bar.label} className="public-home-premium__process-bar">
                  <div className="flex items-center justify-between gap-3">
                    <span>{bar.label}</span>
                    <strong>{bar.value}%</strong>
                  </div>
                  <div className="public-home-premium__process-bar-track" aria-hidden="true">
                    <i style={{ width: `${bar.value}%` }} />
                  </div>
                </div>
              ))}
            </div>

            <div className="public-home-premium__process-evidence-grid">
              <article className="public-home-premium__process-evidence-card">
                <span>Freigaben</span>
                <strong>{resolvedCount} / {preset.checklist.length}</strong>
                <p>Bereits belastbar geführte Signale</p>
              </article>
              <article className="public-home-premium__process-evidence-card">
                <span>Offene Punkte</span>
                <strong>{openCount}</strong>
                <p>Lücken und nächste Prüfpfade bleiben sichtbar</p>
              </article>
              <article className="public-home-premium__process-evidence-card">
                <span>Prozessspur</span>
                <strong>{preset.activity[0]}</strong>
                <p>Aktiver Übergang im aktuellen Schritt</p>
              </article>
            </div>

            <div className="public-home-premium__process-workspace-grid">
              {preset.detailCards.map((card) => (
                <article key={card.label} className="public-home-premium__process-detail-card">
                  <span className="public-home-premium__mini-label">{card.label}</span>
                  <ul className="public-home-premium__process-detail-list">
                    {card.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </div>

        <div className="public-home-premium__process-footer">
          <div className="public-home-premium__process-activity">
            {preset.activity.map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>

          <div className="public-home-premium__process-meter" aria-hidden="true">
            <span style={{ width: `${((index + 1) / total) * 100}%` }} />
          </div>
        </div>
      </motion.article>
    </AnimatePresence>
  );
}

function StaticProcessCard({ step, index, total }) {
  return (
    <article className="public-home-premium__process-static-card">
      <div className="public-home-premium__process-step-copy">
        <span className="public-home-premium__process-step-index">{step.step}</span>
        <div>
          <h3>{step.title}</h3>
          <p>{step.description}</p>
        </div>
      </div>
      <ProcessVisual step={step} index={index} total={total} reducedMotion />
    </article>
  );
}

export default function HomeProcessStory({ content }) {
  const sectionRef = useRef(null);
  const pinRef = useRef(null);
  const stepRefs = useRef([]);
  const visualWrapRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [connectorMetrics, setConnectorMetrics] = useState(null);
  const { reducedMotion, isMobile } = useMotionPreferences();
  const activeStep = content.steps[activeIndex] || content.steps[0];
  const enablePin = !reducedMotion && !isMobile;

  const snapPoints = useMemo(() => {
    const total = Math.max(content.steps.length - 1, 1);
    return content.steps.map((_, index) => index / total);
  }, [content.steps]);

  useLayoutEffect(() => {
    if (!enablePin || !sectionRef.current || !pinRef.current) {
      return undefined;
    }

    const totalSteps = content.steps.length;
    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: sectionRef.current,
        pin: pinRef.current,
        start: "top top+=112",
        end: () => `+=${window.innerHeight * totalSteps * 0.88}`,
        scrub: 0.2,
        snap: snapPoints,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          const nextIndex = Math.min(totalSteps - 1, Math.round(self.progress * (totalSteps - 1)));
          setActiveIndex((current) => (current === nextIndex ? current : nextIndex));
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [content.steps, enablePin, snapPoints]);

  useLayoutEffect(() => {
    if (!enablePin || !pinRef.current || !visualWrapRef.current || !stepRefs.current[activeIndex]) {
      setConnectorMetrics(null);
      return undefined;
    }

    const updateConnector = () => {
      const containerRect = pinRef.current.getBoundingClientRect();
      const activeStepRect = stepRefs.current[activeIndex]?.getBoundingClientRect();
      const visualRect = visualWrapRef.current?.getBoundingClientRect();
      const sidebarPanel = visualWrapRef.current?.querySelector(".public-home-premium__process-sidebar-panel");
      const sidebarRect = sidebarPanel?.getBoundingClientRect();

      if (!visualRect || !activeStepRect || !sidebarRect) {
        setConnectorMetrics(null);
        return;
      }

      const x1 = activeStepRect.right - containerRect.left + 2;
      const y1 = activeStepRect.top - containerRect.top + (activeStepRect.height / 2);
      const spineX = visualRect.left - containerRect.left - 18;
      const x2 = sidebarRect.left - containerRect.left + 26;
      const y2 = sidebarRect.top - containerRect.top + (sidebarRect.height * 0.5);

      setConnectorMetrics({
        horizontalStart: {
          left: x1,
          top: y1,
          width: Math.max(spineX - x1, 0),
        },
        vertical: {
          left: spineX,
          top: Math.min(y1, y2),
          height: Math.abs(y2 - y1),
        },
        horizontalEnd: {
          left: spineX,
          top: y2,
          width: Math.max(x2 - spineX, 0),
        },
        arrow: {
          left: x2,
          top: y2,
        },
      });
    };

    updateConnector();

    const resizeObserver = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(() => updateConnector())
      : null;

    if (resizeObserver) {
      resizeObserver.observe(pinRef.current);
      resizeObserver.observe(visualWrapRef.current);
      stepRefs.current.forEach((node) => {
        if (node) {
          resizeObserver.observe(node);
        }
      });
    }

    window.addEventListener("resize", updateConnector);

    return () => {
      window.removeEventListener("resize", updateConnector);
      resizeObserver?.disconnect();
    };
  }, [activeIndex, content.steps.length, enablePin]);

  return (
    <section className="public-home-premium__section public-home-premium__section--graphite" id="system">
      <div className="mx-auto flex w-full max-w-page flex-col gap-10 px-4 sm:px-6 lg:px-8">
        <HomeSectionHeading
          eyebrow={content.eyebrow}
          title={content.title}
          lead={content.lead}
          tone="inverse"
          size="feature"
          className="public-home-premium__section-heading--process"
        />

        {enablePin ? (
          <div ref={sectionRef} className="public-home-premium__process-shell">
            <div ref={pinRef} className="public-home-premium__process-pin">
              <MotionReveal className="public-home-premium__process-steps" y={24}>
                {(content.steps || []).map((step, index) => {
                  const isActive = activeIndex === index;
                  return (
                    <button
                      key={step.step}
                      ref={(node) => {
                        stepRefs.current[index] = node;
                      }}
                      type="button"
                      className={`public-home-premium__process-step ${isActive ? "is-active" : ""}`.trim()}
                      aria-pressed={isActive}
                      onClick={() => setActiveIndex(index)}
                    >
                      <span className="public-home-premium__process-step-index">{step.step}</span>
                      <div>
                        <h3>{step.title}</h3>
                        <p>{step.description}</p>
                      </div>
                    </button>
                  );
                })}
              </MotionReveal>

              {connectorMetrics ? (
                <div className="public-home-premium__process-connector-overlay" aria-hidden="true">
                  <span
                    className="public-home-premium__process-connector-segment public-home-premium__process-connector-segment--horizontal"
                    style={{
                      left: `${connectorMetrics.horizontalStart.left}px`,
                      top: `${connectorMetrics.horizontalStart.top}px`,
                      width: `${connectorMetrics.horizontalStart.width}px`,
                    }}
                  />
                  <span
                    className="public-home-premium__process-connector-segment public-home-premium__process-connector-segment--vertical"
                    style={{
                      left: `${connectorMetrics.vertical.left}px`,
                      top: `${connectorMetrics.vertical.top}px`,
                      height: `${connectorMetrics.vertical.height}px`,
                    }}
                  />
                  <span
                    className="public-home-premium__process-connector-segment public-home-premium__process-connector-segment--horizontal"
                    style={{
                      left: `${connectorMetrics.horizontalEnd.left}px`,
                      top: `${connectorMetrics.horizontalEnd.top}px`,
                      width: `${connectorMetrics.horizontalEnd.width}px`,
                    }}
                  />
                  <span
                    className="public-home-premium__process-connector-arrow"
                    style={{
                      left: `${connectorMetrics.arrow.left}px`,
                      top: `${connectorMetrics.arrow.top}px`,
                    }}
                  />
                </div>
              ) : null}

              <div ref={visualWrapRef} className="public-home-premium__process-visual-wrap">
                <ProcessVisual
                  step={activeStep}
                  index={activeIndex}
                  total={content.steps.length}
                  reducedMotion={reducedMotion}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="grid gap-4">
            {(content.steps || []).map((step, index) => (
              <StaticProcessCard
                key={step.step}
                step={step}
                index={index}
                total={content.steps.length}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
